import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { LAYER } from '../components/layers';
import { SearchGroup } from '../components/index-page/SearchGroup';
import { Tooltip } from '../components/Tooltip';
import { useAuth } from '../auth/AuthContext';
import { NAV_GROUP_LABEL, searchNav, type NavSearchResult } from './sidebarConfig';

/**
 * The rail's own search — the FIRST thing in the sidebar (product call, 26 Oct 2026).
 *
 * It searches the NAVIGATION, not records: the reader names a page and goes to it. A global
 * record search is a different feature with a backend behind it, and was declined here rather
 * than half-built.
 *
 * **Permission is not this component's business, and that is the point.** Every result comes
 * from `searchNav`, which filters through `isNavNodeVisible` — the same function both rails
 * render through. There is no second answer to "what can this account reach" for the search to
 * get wrong, and a destination the reader would be refused at cannot appear.
 *
 * **The INPUT is `<SearchGroup>`**, the control the filter rail already searches its own
 * entries with, `total` omitted for the same reason: these are destinations rather than
 * records, so there is no count that would be true of them, and the segment would take width
 * a rail this narrow needs for the field.
 */

/** Matches `SidebarFlyout` — the collapsed rail opens panels at this width. */
const PANEL_WIDTH = 288;
const VIEWPORT_MARGIN = 8;

/**
 * ROVING FOCUS, not `aria-activedescendant` (26 Oct 2026).
 *
 * ArrowDown from the field moves real DOM focus onto the first result; the arrows walk the
 * list; Enter activates it natively, because every result IS an `<a>`. The alternative — a
 * combobox with `role="option"` rows — would have overridden the link semantics a screen
 * reader needs and taken over Enter with a programmatic navigate, to land in the same place.
 *
 * It also keeps the product's own rule that a navigation row is a LINK, so middle-click, copy
 * link address and the back button all still work.
 *
 * The lookup is a DOCUMENT query rather than drilled refs because only one search list is ever
 * mounted: the expanded rail renders results in place, the collapsed rail renders them in its
 * panel, and the two are mutually exclusive branches of the same component.
 */
const RESULT_ATTR = 'data-sidebar-search-result';

function resultLinks(): HTMLAnchorElement[] {
  return Array.from(document.querySelectorAll<HTMLAnchorElement>(`a[${RESULT_ATTR}]`));
}

/** ArrowDown from the field steps into the list. Everything else is the field's own. */
function onFieldKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
  if (event.key !== 'ArrowDown') return;

  const first = resultLinks()[0];
  if (!first) return;

  event.preventDefault();
  first.focus();
}

/**
 * **Ctrl/Cmd + K focuses the search, from anywhere** (26 Oct 2026).
 *
 * The de-facto standard for "focus search" — the muscle memory already exists, and browsers
 * let a page claim it. `/` was the alternative and was rejected: this product is forms end to
 * end, so a bare key would fire mid-sentence in any field that did not guard it.
 *
 * **It is refused while the reader is typing**, so Ctrl+K inside a field does whatever that
 * field does. Checked on the ELEMENT rather than a flag, because the one place it must not
 * steal focus from is the one the caret is in — including a `contentEditable` rich-text body,
 * which is neither an input nor a textarea.
 *
 * The COLLAPSED rail has no field to focus, so it opens the panel instead and the panel's own
 * autofocus takes it from there — one shortcut, both states.
 */
function useSearchShortcut(onOpenCollapsed: () => void, collapsed: boolean) {
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'k' && event.key !== 'K') return;
      if (!event.ctrlKey && !event.metaKey) return;

      const active = document.activeElement as HTMLElement | null;
      const typing =
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        active?.isContentEditable === true;

      // The search's OWN field is the exception: Ctrl+K there re-selects rather than refusing.
      const inSearch = active?.id === 'sidebar-search' || active?.id === 'sidebar-flyout-search';
      if (typing && !inSearch) return;

      event.preventDefault();

      if (collapsed) {
        onOpenCollapsed();
        return;
      }

      const field = document.getElementById('sidebar-search') as HTMLInputElement | null;
      field?.focus();
      field?.select();
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [collapsed, onOpenCollapsed]);
}

export function SidebarSearch({
  value,
  onChange,
  collapsed,
}: {
  value: string;
  onChange: (next: string) => void;
  collapsed: boolean;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const close = useCallback(() => {
    setOpen(false);
    onChange('');
  }, [onChange]);

  const openCollapsed = useCallback(() => setOpen(true), []);
  useSearchShortcut(openCollapsed, collapsed);

  /*
    COLLAPSED, the field has nowhere to live — 64px of rail is an icon and nothing else — so
    the icon opens a floating panel holding the field AND its results (product call, 26 Oct
    2026, over expanding the rail or hiding the search entirely). It is the same move the
    collapsed rail already makes for a group, so the reader meets one behaviour rather than two.
  */
  if (collapsed) {
    return (
      <>
        <Tooltip label="Search pages (Ctrl K)">
          <button
            ref={triggerRef}
            type="button"
            id="sidebar-search-trigger"
            aria-label="Search pages"
            aria-expanded={open}
            aria-haspopup="dialog"
            onClick={() => setOpen((wasOpen) => !wasOpen)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-slate-600 transition hover:bg-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
          >
            <Search className="h-5 w-5" aria-hidden="true" />
          </button>
        </Tooltip>

        {open ? (
          <SearchPanel
            value={value}
            onChange={onChange}
            triggerRef={triggerRef}
            onRequestClose={close}
          />
        ) : null}
      </>
    );
  }

  /*
    FLUSH TO THE RAIL (product call, 26 Oct 2026) — no inset of its own, no box and no vertical
    padding. It IS the top edge of the nav region, so a rounded card floating inside a margin
    read as a control dropped onto the rail rather than part of it, and `flush` drops the
    border to a single bottom rule.

    It sits in `DashboardLayout`'s `shrink-0` header, ABOVE the scrolling region rather than
    sticky inside it — the scroller owns the `py-3` and the 8px track, both of which used to
    run down the side of this field. So there is nothing here to cancel and nothing to stick:
    a `pb-*` added here would simply push the first suite heading down.
  */
  return (
    <SearchGroup
      id="sidebar"
      value={value}
      onChange={onChange}
      onKeyDown={onFieldKeyDown}
      placeholder="Search pages (Ctrl K)"
      flush
    />
  );
}

/**
 * The collapsed rail's search panel.
 *
 * Its close handling is `SidebarFlyout`'s, deliberately: Escape restores focus to the trigger,
 * a pointer outside closes, and the rail SCROLLING closes rather than the panel chasing a
 * trigger that has moved. Written out rather than shared because the flyout renders a nav
 * node's children and this renders a field over results — the same behaviour, not the same
 * contents.
 */
function SearchPanel({
  value,
  onChange,
  triggerRef,
  onRequestClose,
}: {
  value: string;
  onChange: (next: string) => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  onRequestClose: () => void;
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);

  const place = useCallback(
    (element: HTMLDivElement | null) => {
      panelRef.current = element;

      const trigger = triggerRef.current;
      if (!element || !trigger) return;

      const bounds = trigger.getBoundingClientRect();
      const height = element.offsetHeight;
      const maxTop = Math.max(VIEWPORT_MARGIN, window.innerHeight - height - VIEWPORT_MARGIN);

      // Anchored to the RAIL's right edge, not the 40px trigger's — see `SidebarFlyout`.
      const rail = trigger.closest('aside');
      const left = (rail ?? trigger).getBoundingClientRect().right + 4;

      element.style.top = `${Math.min(bounds.top, maxTop)}px`;
      element.style.left = `${left}px`;

      element.querySelector<HTMLInputElement>('input')?.focus();
    },
    [triggerRef],
  );

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onRequestClose();
        triggerRef.current?.focus();
      }
    }

    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (!panelRef.current?.contains(target) && !triggerRef.current?.contains(target)) {
        onRequestClose();
      }
    }

    function onScroll(event: Event) {
      if (!panelRef.current?.contains(event.target as Node)) onRequestClose();
    }

    function onResize() {
      onRequestClose();
    }

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [onRequestClose, triggerRef]);

  return createPortal(
    <div
      ref={place}
      role="dialog"
      aria-label="Search pages"
      style={{ position: 'fixed', width: PANEL_WIDTH }}
      className={`modern-scrollbar ${LAYER.PANEL} max-h-[80vh] overflow-y-auto rounded-panel border border-slate-200 bg-white p-2 shadow-raised`}
    >
      <SearchGroup
        id="sidebar-flyout"
        value={value}
        onChange={onChange}
        onKeyDown={onFieldKeyDown}
        placeholder="Search pages (Ctrl K)"
      />

      <div className="pt-2">
        <SidebarSearchResults
          query={value}
          inputId="sidebar-flyout-search"
          onQueryChange={onChange}
          onNavigate={onRequestClose}
        />
      </div>
    </div>,
    document.body,
  );
}

/**
 * The results, grouped under their suite — the rail's own arrangement, so a reader who has
 * learned where a page lives recognises the answer rather than reading a flat list.
 *
 * The TRAIL (`Organization › Settings`) is what makes a bare label legible: four modules ship
 * an entry called `Settings`, and without the path the reader is choosing between four
 * identical rows.
 */
export function SidebarSearchResults({
  query,
  inputId,
  onQueryChange,
  onNavigate,
}: {
  query: string;
  /** Where ArrowUp off the first row returns to, so the reader can keep typing. */
  inputId: string;
  /** Typing on a row edits the query — see `onRowKeyDown`. */
  onQueryChange: (next: string) => void;
  onNavigate?: () => void;
}) {
  const { hasPermission } = useAuth();
  const results: NavSearchResult[] = searchNav(query, hasPermission);

  /** Puts focus back in the field with the caret at the end, ready to keep typing. */
  function returnToField(next?: string) {
    if (next !== undefined) onQueryChange(next);

    const field = document.getElementById(inputId) as HTMLInputElement | null;
    if (!field) return;

    field.focus();
    // After a programmatic focus the caret can land at 0; typing would then insert backwards.
    requestAnimationFrame(() => {
      const end = field.value.length;
      field.setSelectionRange(end, end);
    });
  }

  function onRowKeyDown(event: React.KeyboardEvent<HTMLAnchorElement>) {
    /*
      TYPING ON A ROW GOES BACK TO THE FIELD (26 Oct 2026). A reader who has arrowed into the
      list and then types is refining the query, not acting on the row — so the keystroke lands
      where they meant it to and focus follows it.

      Done EXPLICITLY — preventDefault, edit the value, move focus — rather than focusing the
      field and trusting the browser to retarget the character it is already dispatching. That
      does work in Chrome and is not something to rest a keyboard path on.

      A MODIFIED key is left alone: Ctrl+C on a row is a copy, not the letter `c`.
    */
    if (!event.ctrlKey && !event.metaKey && !event.altKey) {
      if (event.key.length === 1) {
        event.preventDefault();
        returnToField(query + event.key);
        return;
      }

      // Backspace is the other half of typing — correcting is editing the query too.
      if (event.key === 'Backspace') {
        event.preventDefault();
        returnToField(query.slice(0, -1));
        return;
      }
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    const links = resultLinks();
    const index = links.indexOf(event.currentTarget);
    if (index === -1) return;

    event.preventDefault();

    if (event.key === 'ArrowDown') {
      // Stops at the end rather than wrapping: a list that loops gives a reader holding the
      // key no sense of having reached the bottom.
      links[Math.min(index + 1, links.length - 1)]?.focus();
      return;
    }

    /*
      ArrowUp off the FIRST row goes back to the field, not nowhere. The reader arrowed in to
      look and is now refining the query — stranding focus on a row they cannot type into is
      the dead end this whole interaction exists to avoid.
    */
    if (index === 0) returnToField();
    else links[index - 1]?.focus();
  }

  if (results.length === 0) {
    /*
      It says nothing MATCHED, never "you have no access" — the search cannot tell the two
      apart, and guessing at the second would tell a reader their permissions are wrong when
      they have simply mistyped.
    */
    return (
      <p className="px-2 py-3 text-xs text-slate-500">
        No pages match <span className="font-medium text-slate-600">{query.trim()}</span>.
      </p>
    );
  }

  return (
    <div>
      {results.map((result) => (
        <div key={result.suite} className="mb-3 last:mb-0">
          <p className={`px-2 pb-1.5 ${NAV_GROUP_LABEL}`}>{result.suite}</p>

          <ul className="flex flex-col gap-0.5">
            {result.hits.map((hit) => {
              const Icon = hit.node.icon;

              return (
                <li key={hit.node.key}>
                  <Link
                    to={hit.node.to!}
                    onClick={onNavigate}
                    onKeyDown={onRowKeyDown}
                    {...{ [RESULT_ATTR]: 'true' }}
                    /*
                      The focused row takes the SAME tint hover gives it, not a second
                      treatment — one "this is the row you are on", whether the reader got
                      there with a pointer or the arrows.
                    */
                    className="flex min-w-0 items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm text-slate-600 transition hover:bg-slate-200 hover:text-slate-900 focus:bg-slate-200 focus:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
                  >
                    {/* The glyph spans both lines, like a list row's avatar — the row is one
                        `items-center`, so neither line needs an alignment class of its own. */}
                    {Icon ? <Icon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" /> : null}

                    <span className="flex min-w-0 flex-1 flex-col">
                      {/*
                        THE LABEL TAKES THE WHOLE WIDTH and the trail goes UNDER it. Beside it,
                        the trail won: in a 240px rail `Budget Plans` and `Budget Review` both
                        clipped to `Budget …`, so the disambiguator made the two rows it exists
                        to tell apart identical. The label is what the reader is choosing by.
                      */}
                      <span className="truncate">{hit.node.label}</span>

                      {hit.trail.length > 0 ? (
                        /* `aria-hidden`: a visual disambiguator — a screen reader already hears
                           the suite heading above the group, and the label is the link's name. */
                        <span aria-hidden="true" className="truncate text-[11px] text-slate-400">
                          {hit.trail.join(' › ')}
                        </span>
                      ) : null}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}
